import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../../lib/GlobalVar';
import { ConsultaLogins, CriaUsuario, AtualizaUsuario, DefineSenha, BloqueiaUsuario } from '../../lib/api';
import { SyncUsuCandidatos, SyncUsuImportar } from '../../personalizados/api';
import { encrypt } from '../../lib/aes';
import Swal from 'sweetalert2';
import Icon from '../Icon';
import { useNavigate } from 'react-router-dom';
import { podeVerComo } from '../../lib/verComo';

const TABS = [
    { id: 'todos',    label: 'Todos',    match: () => true },
    { id: 'ti-admin', label: 'TI · Admin', match: u => u.setor === 'TI' || u.cargo === 'Administrador' },
    { id: 'inativos', label: 'Inativos', match: u => !u.ativo }
];

const SETORES_PADRAO = ['TI', 'Comercial', 'Marketing', 'Financeiro', 'Operacoes', 'Logistica', 'Relacionamento', 'Diretoria'];
const CARGOS_PADRAO = ['Administrador', 'Gerente', 'Operador', 'Analista', 'Estagiário'];

function initials(nome) {
    const parts = (nome || '').trim().split(/\s+/);
    return ((parts[0] || '?').slice(0, 1) + (parts[1] || '').slice(0, 1)).toUpperCase();
}

function loadCustom(key, fallback) {
    try {
        const raw = localStorage.getItem('centralbabita.' + key);
        if (!raw) return fallback;
        const arr = JSON.parse(raw);
        return Array.isArray(arr) && arr.length ? arr : fallback;
    } catch {
        return fallback;
    }
}

function saveCustom(key, arr) {
    localStorage.setItem('centralbabita.' + key, JSON.stringify(arr));
}

export default function TILogins() {
    const { user: meUser, verComo } = useContext(GlobalContext);
    const navigate = useNavigate();
    const mostraVerComo = podeVerComo(meUser);

    // "Ver como" (lib/verComo.js): o Central passa a mostrar o menu, as telas e as permissoes de
    // `u`. Gravacoes ficam bloqueadas nesse modo; a faixa no topo traz de volta.
    async function verComoUsuario(u) {
        const e = (t) => String(t == null ? '' : t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
        const ok = await Swal.fire({
            icon: 'question', title: `Ver o sistema como ${u.nome}?`,
            html: `Você vai ver o menu, as telas e as permissões de <b>${e(u.nome)}</b>`
                + ` (${e([u.setor, u.cargo].filter(Boolean).join(' · '))}).<br/><br/>`
                + 'Nesse modo, <b>ações que gravam ficam bloqueadas</b>. Para voltar, use a faixa no topo da tela.',
            showCancelButton: true, confirmButtonText: 'Ver como', cancelButtonText: 'Cancelar',
        });
        if (!ok.isConfirmed) return;
        if (verComo(u)) navigate('/Dashboard');
    }
    const [tab, setTab] = useState('todos');
    const [users, setUsers] = useState([]);
    const [modal, setModal] = useState(null);
    const [setores, setSetores] = useState(() => loadCustom('setores', SETORES_PADRAO));
    const [cargos, setCargos] = useState(() => loadCustom('cargos', CARGOS_PADRAO));

    useEffect(() => { reload(); }, []);
    useEffect(() => { saveCustom('setores', setores); }, [setores]);
    useEffect(() => { saveCustom('cargos', cargos); }, [cargos]);

    async function reload() {
        const resp = await ConsultaLogins({});
        if (resp.Error) return setUsers([]);
        const list = Array.isArray(resp) ? resp : (resp.users || []);
        setUsers(list);
        // Aprender setores/cargos novos vindos do backend
        const novosSetores = [...new Set([...setores, ...list.map(u => u.setor).filter(Boolean)])];
        const novosCargos = [...new Set([...cargos, ...list.map(u => u.cargo).filter(Boolean)])];
        if (novosSetores.length !== setores.length) setSetores(novosSetores);
        if (novosCargos.length !== cargos.length) setCargos(novosCargos);
    }

    async function toggleAtivo(u) {
        const c = await Swal.fire({
            icon: 'question',
            title: u.ativo ? `Bloquear ${u.nome}?` : `Reativar ${u.nome}?`,
            showCancelButton: true,
            confirmButtonText: u.ativo ? 'Bloquear' : 'Reativar'
        });
        if (!c.isConfirmed) return;
        const resp = await BloqueiaUsuario({ solicitante: meUser?.email, id: u.id, ativo: !u.ativo });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        reload();
    }

    const counts = TABS.reduce((acc, t) => ({ ...acc, [t.id]: users.filter(t.match).length }), {});
    const visiveis = users.filter(TABS.find(t => t.id === tab).match);

    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow"><span className="num ti">TI</span> Logins &amp; Usuários</div>
                    <h1 className="page-title">Logins &amp; Usuários</h1>
                    <p className="page-sub">
                        <span className="mono">logins.json</span> cifrado em AES. Senhas em MD5. Toda ação auditada.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <button className="btn ghost" onClick={() => setModal({ tipo: 'setores' })}>Setores &amp; Cargos</button>
                    <button className="btn ghost" onClick={() => setModal({ tipo: 'importar-erp' })} title="Buscar usuários no ERP (cloud) que ainda não existem no Central e importar os que você escolher">Importar do ERP</button>
                    <button className="btn" onClick={() => setModal({ tipo: 'novo' })}>
                        <Icon name="plus" size={11}/> Novo usuário
                    </button>
                </div>
            </div>

            <div className="tabs">
                {TABS.map(t => (
                    <button key={t.id} className={`tab ${tab === t.id ? 'active' : ''}`} onClick={() => setTab(t.id)}>
                        {t.label.toUpperCase()} <span className="tab-count">{String(counts[t.id]).padStart(2, '0')}</span>
                    </button>
                ))}
            </div>

            <div className="card flat" style={{ padding: 0 }}>
                <div className="users-list">
                    {visiveis.map(u => (
                        <div className="user-row" key={u.id}>
                            <div className="avatar lg">{initials(u.nome)}</div>
                            <div className="user-info">
                                <div className="user-name">{u.nome}</div>
                                <div className="user-mail mono">{u.email}</div>
                            </div>
                            <div className="user-role">
                                <div>{u.cargo}</div>
                                <div className="muted">{u.setor}{u.loja != null ? ` · Loja ${u.loja_nome || u.loja}` : ''}</div>
                                {u.codigo_vendedora != null && <div className="muted" style={{ fontSize: 11 }}>Cód. vendedora: <b>{u.codigo_vendedora}</b></div>}
                                {u.celular && <div className="muted" style={{ fontSize: 11 }}>Celular: <b>{u.celular}</b></div>}
                                {(u.vinculo_fornecedor != null || u.fornecedor_nome) && <div className="muted" style={{ fontSize: 11 }}>Vínculo forn.: <b>{u.vinculo_fornecedor != null ? u.vinculo_fornecedor : '—'}</b>{u.fornecedor_nome ? ` (${u.fornecedor_nome})` : ''}</div>}
                            </div>
                            <div className="user-status">
                                <span className={`dot ${u.ativo ? 'ok' : 'err'}`}/>
                                <span>{u.ativo ? 'ATIVO' : 'BLOQUEADO'}</span>
                                {u.must_change && <div className="muted" style={{ fontSize: 11 }}>TROCAR SENHA</div>}
                                {u.restrito && <div className="muted" style={{ fontSize: 11 }}>ACESSO RESTRITO</div>}
                            </div>
                            <div className="user-actions">
                                {mostraVerComo && u.id !== meUser.id && (
                                    <button className="btn ghost sm" onClick={() => verComoUsuario(u)}
                                        title="Ver o sistema com o acesso deste usuário (menu, telas e permissões). Ações que gravam ficam bloqueadas.">Ver como</button>
                                )}
                                <button className="btn ghost sm" onClick={() => setModal({ tipo: 'senha', user: u })}>Definir senha</button>
                                <button className="btn ghost sm" onClick={() => setModal({ tipo: 'editar', user: u, setores, cargos })}>Editar</button>
                                <button className="btn ghost sm" onClick={() => setModal({ tipo: 'duplicar', user: u, setores, cargos })} title="Criar um novo usuário com as mesmas configurações (setor, cargo, status)">Duplicar</button>
                                <label className="toggle" title={u.ativo ? 'Bloquear' : 'Reativar'}>
                                    <input type="checkbox" checked={!!u.ativo} onChange={() => toggleAtivo(u)}/>
                                    <span className="toggle-slider"/>
                                </label>
                            </div>
                        </div>
                    ))}
                    {visiveis.length === 0 && <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Sem usuários nesta aba.</div>}
                </div>
            </div>

            {modal && modal.tipo === 'setores' && (
                <SetoresModal
                    setores={setores} setSetores={setSetores}
                    cargos={cargos} setCargos={setCargos}
                    onClose={() => setModal(null)}
                />
            )}
            {modal && (modal.tipo === 'novo' || modal.tipo === 'editar' || modal.tipo === 'duplicar') && (
                <UsuarioModal
                    tipo={modal.tipo}
                    user={modal.user}
                    setores={setores}
                    cargos={cargos}
                    me={meUser}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); reload(); }}
                />
            )}
            {modal && modal.tipo === 'senha' && (
                <SenhaModal
                    user={modal.user}
                    me={meUser}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); reload(); }}
                />
            )}
            {modal && modal.tipo === 'importar-erp' && (
                <ImportarErpModal me={meUser} onClose={() => setModal(null)} onSaved={() => { setModal(null); reload(); }} />
            )}
        </div>
    );
}

function UsuarioModal({ tipo, user, setores, cargos, me, onClose, onSaved }) {
    // 'duplicar' cria um novo usuario reaproveitando as configuracoes do usuario-origem
    // (setor, cargo, status, troca-de-senha). Identidade (nome/e-mail/login) e senha sao
    // sempre preenchidas do zero por serem unicas.
    const criando = tipo === 'novo' || tipo === 'duplicar';
    const [form, setForm] = useState({
        nome: tipo === 'editar' ? (user?.nome || '') : '',
        email: tipo === 'editar' ? (user?.email || '') : '',
        login: tipo === 'editar' ? (user?.login || '') : '',
        setor: user?.setor || setores[0] || 'TI',
        cargo: user?.cargo || cargos[0] || 'Operador',
        codigo_vendedora: user?.codigo_vendedora != null ? String(user.codigo_vendedora) : '',
        loja: user?.loja != null ? String(user.loja) : '',
        celular: user?.celular != null ? String(user.celular) : '',
        vinculo_fornecedor: user?.vinculo_fornecedor != null ? String(user.vinculo_fornecedor) : '',
        fornecedor_nome: user?.fornecedor_nome || '',
        senha: '',
        must_change: criando ? true : !!user?.must_change,
        ativo: user?.ativo !== false,
        restrito: !!user?.restrito
    });

    function up(k, v) { setForm({ ...form, [k]: v }); }

    async function salvar() {
        if (!form.nome || !form.email) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Nome e e-mail são obrigatórios.' });
        const login = form.login || form.email.split('@')[0];
        if (criando) {
            if (!form.senha) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Informe a senha.' });
            const resp = await CriaUsuario({
                solicitante: me?.email,
                nome: form.nome,
                email: form.email,
                login,
                setor: form.setor,
                cargo: form.cargo,
                codigo_vendedora: form.codigo_vendedora,
                loja: form.loja,
                celular: form.celular,
                vinculo_fornecedor: form.vinculo_fornecedor,
                fornecedor_nome: form.fornecedor_nome,
                ativo: form.ativo,
                SENHA: encrypt(form.senha),
                must_change: form.must_change,
                restrito: form.restrito
            });
            if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
            Swal.fire({ icon: 'success', title: 'Usuário criado', timer: 1100, showConfirmButton: false });
            return onSaved();
        }
        // editar
        const resp = await AtualizaUsuario({
            solicitante: me?.email,
            id: user.id,
            nome: form.nome,
            email: form.email,
            login,
            setor: form.setor,
            cargo: form.cargo,
            codigo_vendedora: form.codigo_vendedora,
            loja: form.loja,
            celular: form.celular,
            vinculo_fornecedor: form.vinculo_fornecedor,
            fornecedor_nome: form.fornecedor_nome,
            ativo: form.ativo,
            must_change: form.must_change,
            restrito: form.restrito
        });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        Swal.fire({ icon: 'success', title: 'Usuário atualizado', timer: 1100, showConfirmButton: false });
        onSaved();
    }

    return (
        <div className="modal-mask">
            <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="eyebrow">{tipo === 'novo' ? 'Novo usuário' : tipo === 'duplicar' ? `Duplicar · ${user?.nome}` : `Editar · ${user?.nome}`}</div>
                    <button className="icon-btn" onClick={onClose}><Icon name="x" size={14}/></button>
                </div>
                <div className="modal-body">
                    {tipo === 'duplicar' && (
                        <p className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
                            Copiando setor, cargo e status de <strong>{user?.nome}</strong>. Defina nome, e-mail, login e senha do novo usuário.
                        </p>
                    )}
                    <div className="field"><label>Nome</label><input className="input" value={form.nome} onChange={e => up('nome', e.target.value)}/></div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div className="field"><label>E-mail</label><input className="input" value={form.email} onChange={e => up('email', e.target.value)} placeholder="nome@babita.com.br"/></div>
                        <div className="field"><label>Login</label><input className="input mono" value={form.login} onChange={e => up('login', e.target.value.toLowerCase())} placeholder="(auto = parte antes do @)"/></div>
                    </div>
                    {/* Celular vale para qualquer setor (nao so Comercial): e por ele que o
                        atendimento no WhatsApp sabe quem esta falando. So digitos. */}
                    <div className="field"><label>Celular (WhatsApp)</label>
                        <input className="input mono" value={form.celular} onChange={e => up('celular', e.target.value.replace(/\D/g, ''))} placeholder="DDD + numero, ex 31999457727"/></div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <div className="field"><label>Setor</label><select className="select" value={form.setor} onChange={e => up('setor', e.target.value)}>
                            {setores.map(s => <option key={s}>{s}</option>)}
                        </select></div>
                        <div className="field"><label>Cargo</label><select className="select" value={form.cargo} onChange={e => up('cargo', e.target.value)}>
                            {cargos.map(c => <option key={c}>{c}</option>)}
                        </select></div>
                    </div>
                    {form.setor === 'Comercial' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div className="field"><label>Código da vendedora</label>
                                <input className="input mono" value={form.codigo_vendedora} onChange={e => up('codigo_vendedora', e.target.value.replace(/\D/g, ''))} placeholder="nº da vendedora (ven_numero)"/></div>
                            <div className="field"><label>Loja (nº)</label>
                                <input className="input mono" value={form.loja} onChange={e => up('loja', e.target.value.replace(/\D/g, ''))} placeholder="1, 8, 21, 34…"/></div>
                        </div>
                    )}
                    {form.setor === 'Expedicao' && (
                        <div className="field"><label>Loja (nº)</label>
                            <input className="input mono" value={form.loja} onChange={e => up('loja', e.target.value.replace(/\D/g, ''))} placeholder="nº da loja"/></div>
                    )}
                    {form.setor === 'Fornecedores' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div className="field"><label>Vínculo fornecedor (código)</label>
                                <input className="input mono" value={form.vinculo_fornecedor} onChange={e => up('vinculo_fornecedor', e.target.value.replace(/\D/g, ''))} placeholder="código de vínculo (forvin_cod)"/></div>
                            <div className="field"><label>Fornecedor (nome)</label>
                                <input className="input" value={form.fornecedor_nome} onChange={e => up('fornecedor_nome', e.target.value)} placeholder="nome do fornecedor"/></div>
                        </div>
                    )}
                    {criando && (
                        <div className="field">
                            <label>Senha inicial</label>
                            <input className="input" type="password" value={form.senha} onChange={e => up('senha', e.target.value)} placeholder="Qualquer tamanho, sem regra"/>
                        </div>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginTop: 8 }}>
                        <input type="checkbox" checked={form.must_change} onChange={e => up('must_change', e.target.checked)}/>
                        Forçar troca de senha no próximo login
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12, marginTop: 6 }}>
                        <input type="checkbox" checked={form.restrito} onChange={e => up('restrito', e.target.checked)} style={{ marginTop: 2 }}/>
                        <span>Acesso restrito: só vê as páginas liberadas pelo nome do usuário em Páginas Internas — sem Dashboard, Arquivos, Configurações nem as páginas de “todos” ou do setor</span>
                    </label>
                    {tipo === 'editar' && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, marginTop: 6 }}>
                            <input type="checkbox" checked={form.ativo} onChange={e => up('ativo', e.target.checked)}/>
                            Ativo
                        </label>
                    )}
                </div>
                <div className="modal-foot">
                    <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
                    <button className="btn sm" onClick={salvar}>{criando ? 'Criar' : 'Atualizar'}</button>
                </div>
            </div>
        </div>
    );
}

function SenhaModal({ user, me, onClose, onSaved }) {
    const [senha, setSenha] = useState('');
    const [mustChange, setMustChange] = useState(false);  // [iter5] default false: badge 'TROCAR SENHA' some apos redefinir, dando feedback visual claro

    async function salvar() {
        if (!senha) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Informe a senha.' });
        const resp = await DefineSenha({
            solicitante: me?.email,
            id: user.id,
            SENHA: encrypt(senha),
            must_change: mustChange
        });
        if (resp.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: resp.Error });
        Swal.fire({ icon: 'success', title: 'Senha definida', timer: 1100, showConfirmButton: false });
        onSaved();
    }

    return (
        <div className="modal-mask" onClick={onClose}>
            <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="eyebrow">Definir senha · {user?.nome}</div>
                    <button className="icon-btn" onClick={onClose}><Icon name="x" size={14}/></button>
                </div>
                <div className="modal-body">
                    <div className="field">
                        <label>Nova senha</label>
                        <input className="input" type="password" value={senha} onChange={e => setSenha(e.target.value)} placeholder="Qualquer tamanho, sem regra"/>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                        <input type="checkbox" checked={mustChange} onChange={e => setMustChange(e.target.checked)}/>
                        Forçar troca no próximo login
                    </label>
                </div>
                <div className="modal-foot">
                    <button className="btn ghost sm" onClick={onClose}>Cancelar</button>
                    <button className="btn sm" onClick={salvar}>Salvar</button>
                </div>
            </div>
        </div>
    );
}

function SetoresModal({ setores, setSetores, cargos, setCargos, onClose }) {
    const [novoSetor, setNovoSetor] = useState('');
    const [novoCargo, setNovoCargo] = useState('');

    function addSetor() {
        const v = novoSetor.trim();
        if (!v) return;
        if (setores.includes(v)) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Setor já existe.' });
        setSetores([...setores, v]);
        setNovoSetor('');
    }
    function delSetor(s) {
        setSetores(setores.filter(x => x !== s));
    }
    function addCargo() {
        const v = novoCargo.trim();
        if (!v) return;
        if (cargos.includes(v)) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Cargo já existe.' });
        setCargos([...cargos, v]);
        setNovoCargo('');
    }
    function delCargo(c) {
        setCargos(cargos.filter(x => x !== c));
    }

    return (
        <div className="modal-mask" onClick={onClose}>
            <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                <div className="modal-head">
                    <div className="eyebrow">Setores &amp; Cargos</div>
                    <button className="icon-btn" onClick={onClose}><Icon name="x" size={14}/></button>
                </div>
                <div className="modal-body">
                    <p className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
                        Lista local desta Central. Salva no localStorage do navegador. Setores/cargos vindos do backend são aprendidos automaticamente.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                        <div>
                            <div className="eyebrow" style={{ marginBottom: 8 }}>Setores</div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input className="input" value={novoSetor} onChange={e => setNovoSetor(e.target.value)} placeholder="Novo setor…" onKeyDown={e => e.key === 'Enter' && addSetor()}/>
                                <button className="btn sm" onClick={addSetor}><Icon name="plus" size={11}/></button>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                                {setores.map(s => (
                                    <span key={s} className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        {s}
                                        <button onClick={() => delSetor(s)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex' }}><Icon name="x" size={10}/></button>
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="eyebrow" style={{ marginBottom: 8 }}>Cargos</div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input className="input" value={novoCargo} onChange={e => setNovoCargo(e.target.value)} placeholder="Novo cargo…" onKeyDown={e => e.key === 'Enter' && addCargo()}/>
                                <button className="btn sm" onClick={addCargo}><Icon name="plus" size={11}/></button>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                                {cargos.map(c => (
                                    <span key={c} className="chip" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        {c}
                                        <button onClick={() => delCargo(c)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'inline-flex' }}><Icon name="x" size={10}/></button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="modal-foot">
                    <button className="btn sm" onClick={onClose}>Concluir</button>
                </div>
            </div>
        </div>
    );
}

// Botão "Importar do ERP": consulta o cloud (usuários que NÃO existem no Central), escolhe e importa.
function ImportarErpModal({ me, onClose, onSaved }) {
    const [data, setData] = useState(null);
    const [q, setQ] = useState('');
    const [sel, setSel] = useState({});
    const [busy, setBusy] = useState('load');
    useEffect(() => { (async () => { const r = await SyncUsuCandidatos({ solicitante: me?.email }); setBusy(''); if (r && r.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: r.Error }); setData(r); })(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const lista = (data?.lista || []).filter(x => !q || (x.login + ' ' + x.setor + ' ' + (x.fornecedor_nome || '')).toLowerCase().includes(q.toLowerCase()));
    const escolhidos = Object.keys(sel).filter(k => sel[k]).map(Number);
    async function importar() {
        if (!escolhidos.length) return Swal.fire({ icon: 'warning', title: 'Atenção', text: 'Selecione ao menos um usuário.' });
        const dry = await SyncUsuImportar({ solicitante: me?.email, usuCods: escolhidos });
        if (dry && dry.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: dry.Error });
        const c = await Swal.fire({ icon: 'question', title: `Importar ${dry.adicionados} usuário(s)?`, html: 'Senha padrão <b>12345678910</b> (troca no 1º acesso).', showCancelButton: true, confirmButtonText: 'Importar' });
        if (!c.isConfirmed) return;
        setBusy('imp');
        const r = await SyncUsuImportar({ solicitante: me?.email, usuCods: escolhidos, confirmar: 'true' });
        setBusy('');
        if (r && r.Error) return Swal.fire({ icon: 'warning', title: 'Atenção', text: r.Error });
        Swal.fire({ icon: 'success', title: 'Importado', text: `${r.adicionados} conta(s) criada(s).`, timer: 1400, showConfirmButton: false });
        onSaved();
    }
    return (
        <div className="modal-mask">
            <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 660 }}>
                <div className="modal-head">
                    <div className="eyebrow">Importar do ERP · usuários que faltam no Central</div>
                    <button className="icon-btn" onClick={onClose}><Icon name="x" size={14}/></button>
                </div>
                <div className="modal-body">
                    {busy === 'load' && <div className="muted">Consultando o cloud…</div>}
                    {data && (<>
                        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>{data.total} disponível(is) — {Object.entries(data.resumo || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')}. Vendedoras só das lojas 1/8/21/34.</div>
                        <input className="input" placeholder="filtrar por nome, setor, fornecedor…" value={q} onChange={e => setQ(e.target.value)} style={{ marginBottom: 8 }}/>
                        <div style={{ maxHeight: 360, overflow: 'auto', border: '1px solid #e4e1dc', borderRadius: 8 }}>
                            {lista.slice(0, 400).map(x => (
                                <label key={x.usu_cod} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 10px', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                                    <input type="checkbox" checked={!!sel[x.usu_cod]} onChange={e => setSel(s => ({ ...s, [x.usu_cod]: e.target.checked }))}/>
                                    <b style={{ minWidth: 150 }}>{x.login}</b>
                                    <span className="muted">{x.setor}{x.loja != null ? ` · Loja ${x.loja_nome || x.loja}` : ''}{x.codigo_vendedora ? ` · cód ${x.codigo_vendedora}` : ''}{x.vinculo_fornecedor != null ? ` · vínc ${x.vinculo_fornecedor}` : ''}</span>
                                </label>
                            ))}
                            {!lista.length && <div className="muted" style={{ padding: 16 }}>Nada encontrado.</div>}
                        </div>
                    </>)}
                </div>
                <div className="modal-foot" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn ghost sm" onClick={onClose}>Fechar</button>
                    <button className="btn sm" onClick={importar} disabled={busy === 'imp' || !escolhidos.length}>{busy === 'imp' ? 'Importando…' : `Importar selecionados (${escolhidos.length})`}</button>
                </div>
            </div>
        </div>
    );
}
